import { useEffect } from 'react';

export function useSwipe(onSwipeDown: () => void, threshold = 80) {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let startY = 0;
    let activeScrollable: HTMLElement | null = null;
    let startScrollTop = 0;

    const getScrollableFromTarget = (target: EventTarget | null) => {
      const resolveStartingElement = () => {
        if (target instanceof HTMLElement) {
          return target;
        }

        if (target instanceof SVGElement) {
          const ownerParent =
            target.ownerSVGElement?.parentElement ?? target.parentElement;

          return ownerParent as HTMLElement | null;
        }

        return null;
      };

      let el = resolveStartingElement();

      while (el) {
        const style = window.getComputedStyle(el);
        const { overflowY } = style;
        const canScroll =
          el.scrollHeight > el.clientHeight &&
          (overflowY === 'auto' || overflowY === 'scroll');

        if (canScroll) {
          return el;
        }

        el = el.parentElement;
      }

      return document.scrollingElement as HTMLElement | null;
    };

    const getScrollTop = (el: HTMLElement | null) => {
      if (el) {
        return el.scrollTop;
      }

      if (document.scrollingElement) {
        return document.scrollingElement.scrollTop;
      }

      return window.scrollY;
    };

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      activeScrollable = getScrollableFromTarget(e.target);
      startScrollTop = getScrollTop(activeScrollable);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const endY = e.changedTouches[0].clientY;
      const diffY = endY - startY;
      const currentScrollTop = getScrollTop(activeScrollable);
      const TOP_TOLERANCE_PX = 2;
      const isAtTop =
        startScrollTop <= TOP_TOLERANCE_PX &&
        currentScrollTop <= TOP_TOLERANCE_PX;

      if (diffY > threshold && isAtTop) {
        onSwipeDown();
      }

      activeScrollable = null;
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSwipeDown, threshold]);
}
